import React, { useRef } from "react";
import "../graphStyles.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import NoDataFound from "../../NoDataFound";
import { ExportDropdown } from "../../ExportDropdown";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const InfoIcon = ({ tooltipText }) => (
  <span className="info-tooltip-container">
    <svg className="info-tooltip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
    <span className="info-tooltip-bubble">{tooltipText}</span>
  </span>
);

export function VisitorsWhoSawNudgesGraph({ visitorWhoSawNudge, loading }) {
  const chartRef = useRef(null);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Week",
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of unique shoppers",
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },

        ticks: {
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },
      },
      y1: {
        position: "right",
        title: {
          display: true,
          text: "% of visitors who added to cart",
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },
        ticks: {
          font: {
            size: function () {
              if ((window.innerWidth < 1050) & (window.innerWidth > 767)) {
                return 10;
              } else {
                return 13;
              }
            },
          },
        },
      },
    },
  };

  const data = {
    labels: visitorWhoSawNudge.labels,

    datasets: [
      {
        label: "Coupons shown and added to cart",
        data: visitorWhoSawNudge.couponsShownAndAdded,
        backgroundColor: "rgba(230, 210, 20, 0.99)",
        hoverBackgroundColor: "rgba(230, 210, 20, 0.99)",
        order: 1,
      },

      {
        label: "Coupons shown",
        data: visitorWhoSawNudge.couponsShown,
        backgroundColor: "rgba(12, 100, 230, 0.99)",
        hoverBackgroundColor: "rgba(12, 100, 230, 0.99)",
        order: 2,
      },

      {
        label: "% of visitors",
        data: visitorWhoSawNudge.percentOfVisitors,
        type: "line",
        borderColor: "rgba(12, 200, 255, 0.99)",
        backgroundColor: "rgba(12, 200, 255, 0.99)",
        hoverBackgroundColor: "rgba(12, 200, 255, 0.99)",
        yAxisID: "y1",
        lineTension: 0.2,
        order: 0,
      },
    ],
  };

  const allData = [
    ["Metric", ...visitorWhoSawNudge.labels],
    ["% of visitors", ...visitorWhoSawNudge.percentOfVisitors],
    ["Coupons shown and added to cart", ...visitorWhoSawNudge.couponsShownAndAdded],
    ["Coupons shown", ...visitorWhoSawNudge.couponsShown],
  ];

  return (
    <div className="graph flex flex-col">
      <div className="graph__title flex justify-between items-center pr-3">
        <h4 className="text-lg md:text-xl flex items-center gap-1.5">
          <span>Visitors who saw intent nudges</span>
          <InfoIcon tooltipText="Tracks user interaction: showing coupons, coupon click-throughs, and subsequent cart additions." />
        </h4>
        <ExportDropdown
          data={allData}
          chartRef={chartRef}
          name="Visitors_who_Saw_Intent_Nudges"
        />
      </div>
      {loading ? (
        <div className="loader flex-grow flex justify-center items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="185px"
            height="185px"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid"
          >
            <circle
              cx="50"
              cy="50"
              r="0"
              fill="none"
              stroke="#b927d4"
              strokeWidth="1"
            >
              <animate
                attributeName="r"
                repeatCount="indefinite"
                dur="1.2658227848101264s"
                values="0;51"
                keyTimes="0;1"
                keySplines="0 0.2 0.8 1"
                calcMode="spline"
                begin="0s"
              ></animate>
              <animate
                attributeName="opacity"
                repeatCount="indefinite"
                dur="1.2658227848101264s"
                values="1;0"
                keyTimes="0;1"
                keySplines="0.2 0 0.8 1"
                calcMode="spline"
                begin="0s"
              ></animate>
            </circle>
            <circle
              cx="50"
              cy="50"
              r="0"
              fill="none"
              stroke="#32c5ff"
              strokeWidth="1"
            >
              <animate
                attributeName="r"
                repeatCount="indefinite"
                dur="1.2658227848101264s"
                values="0;51"
                keyTimes="0;1"
                keySplines="0 0.2 0.8 1"
                calcMode="spline"
                begin="-0.6329113924050632s"
              ></animate>
              <animate
                attributeName="opacity"
                repeatCount="indefinite"
                dur="1.2658227848101264s"
                values="1;0"
                keyTimes="0;1"
                keySplines="0.2 0 0.8 1"
                calcMode="spline"
                begin="-0.6329113924050632s"
              ></animate>
            </circle>
          </svg>
        </div>
      ) : (
        <>
          {visitorWhoSawNudge.labels &&
          visitorWhoSawNudge.labels.length !== 0 ? (
            <div className="relative h-80 md:h-96">
              <Bar ref={chartRef} className="chart" id="chart15" options={options} data={data} />
            </div>
          ) : (
            <NoDataFound />
          )}
        </>
      )}
    </div>
  );
}
